import type * as T from "../types";
import type { components } from "./schema";

type Schemas = components["schemas"];
export type DocumentPageWire = Schemas["DocumentPage"];
export type DocumentSummaryWire = Schemas["DocumentSummary"];
export type DocumentDetailWire = Schemas["DocumentDetailResponse"];
export type ReceiptPageWire = Schemas["ReceiptPage"];
export type ReceiptSummaryWire = Schemas["ReceiptSummary"];
export type ReceiptDetailWire = Schemas["ReceiptDetailResponse"];

/**
 * The series each kind of document takes its number from. The API makes a caller name one rather
 * than inferring it, because branch-wise numbering is still open (build plan §6 item 7).
 */
export const INVOICE_SERIES = "SALES";
export const CREDIT_NOTE_SERIES = "CN";

const VAT_CATEGORY: Record<Schemas["VatCategory"], T.VatCategory> = {
  standard: "S",
  zeroRated: "Z",
  exempt: "E",
  reverseCharge: "AE",
  intraCommunity: "O",
  freeExport: "O",
  outsideScope: "O",
};

const CATEGORY_ON_THE_WIRE: Record<T.VatCategory, Schemas["VatCategory"]> = {
  S: "standard",
  Z: "zeroRated",
  E: "exempt",
  AE: "reverseCharge",
  O: "outsideScope",
};

const PAYMENT: Record<Schemas["DocumentPayment"], T.PaymentStatus> = {
  unpaid: "unpaid",
  partiallyPaid: "partiallyPaid",
  paid: "paid",
  overdue: "overdue",
};

/** "13.0000" → "13", "1250.5000" → "1250.50": the column's scale, not a fact about the money. */
function money(value: unknown): T.Money {
  const [whole = "0", fraction = ""] = String(value).split(".");
  return `${whole}.${fraction.padEnd(2, "0").slice(0, 2)}`;
}

function plain(value: unknown): string {
  const text = String(value);
  return text.includes(".") ? text.replace(/\.?0+$/, "") : text;
}

export function toInvoiceSummary(wire: DocumentSummaryWire): T.InvoiceSummary {
  return {
    id: wire.id,
    number: wire.number,
    customerId: wire.customerId,
    customerName: wire.customerName,
    date: wire.issueDate,
    // Empty when it has none. Ageing then runs from the invoice date (ADR-0032).
    due: wire.dueDate ?? "",
    status: wire.issued ? "approved" : "draft",
    payment: PAYMENT[wire.payment],
    total: money(wire.total),
    balance: money(wire.outstanding),
    locked: wire.issued,
  };
}

function toLines(wire: DocumentDetailWire): T.InvoiceLine[] {
  return wire.lines.map((line) => ({
    n: Number(line.lineNumber),
    itemName: line.description,
    accountId: line.accountId,
    accountName: line.accountName,
    quantity: plain(line.quantity),
    unitPrice: plain(line.grossPrice),
    vatCategory: VAT_CATEGORY[line.category],
    vatRatePct: line.rate === null ? "0" : plain(line.rate),
    lineNet: money(line.netAmount),
    ...(line.itemCode ? { itemCode: line.itemCode } : {}),
    ...(line.exemptionReason ? { exemptionReason: line.exemptionReason } : {}),
    ...(line.billingItemId ? { billingItemId: line.billingItemId } : {}),
    ...(Number(line.lineCharges) !== 0 ? { charges: money(line.lineCharges) } : {}),
    ...(Number(line.lineAllowances) !== 0 ? { allowances: money(line.lineAllowances) } : {}),
  }));
}

function toTotals({ totals }: DocumentDetailWire): T.DocumentTotals {
  return {
    lineNet: money(totals.lineNet),
    allowances: money(totals.allowances),
    charges: money(totals.charges),
    taxExclusive: money(totals.withoutVat),
    vat: money(totals.vat),
    taxInclusive: money(totals.withVat),
    rounding: money(totals.rounding),
    paid: money(totals.paid),
    due: money(totals.due),
  };
}

/** The person is an id until user names are read; the date is what the history can vouch for today. */
function toHistory(wire: DocumentDetailWire): T.DocumentHistory {
  return {
    createdBy: "",
    createdAt: wire.createdAt,
    ...(wire.issuedAt === null ? {} : { approvedAt: wire.issuedAt }),
  };
}

export function toInvoice(wire: DocumentDetailWire): T.Invoice {
  return {
    ...toInvoiceSummary(wire.summary),
    currency: wire.currencyCode,
    fromQuotation: wire.fromQuotation ?? undefined,
    buyerReference: wire.buyerReference ?? undefined,
    note: wire.note ?? undefined,
    footerText: wire.footerText ?? undefined,
    adjustments: (wire.adjustments ?? []).map((a) => ({
      isCharge: a.isCharge,
      amount: money(a.amount),
      category: VAT_CATEGORY[a.category],
      rate: a.rate === null || a.rate === undefined ? null : plain(a.rate),
      reason: a.reason ?? "",
    })),
    paymentDetails: wire.paymentDetails ?? undefined,
    customerTaxId: wire.customerTaxId ?? undefined,
    customerLegalRegistration: wire.customerLegalRegistration ?? undefined,
    cashSale: wire.cashSale
      ? {
          amountReceived: money(wire.cashSale.amountReceived),
          method: wire.cashSale.paymentMethod as T.ReceiptMethod,
          bankAccountId: wire.cashSale.bankAccountId ?? "",
          bankAccountName: wire.cashSale.bankAccountName ?? undefined,
        }
      : undefined,
    paymentTermsDays: wire.paymentTermsDays == null ? undefined : String(wire.paymentTermsDays),
    lines: toLines(wire),
    totals: toTotals(wire),
    payments: wire.payments.map((p) => ({
      id: p.receiptId,
      number: p.number ?? "",
      date: p.receivedOn,
      amount: money(p.amount),
    })),
    history: toHistory(wire),
  };
}

type QuotationSummaryWire = Schemas["QuotationSummary"];
export type QuotationPageWire = Schemas["QuotationPage"];
export type QuotationDetailWire = Schemas["QuotationDetailResponse"];

export function toQuotationSummary(wire: QuotationSummaryWire): T.QuotationSummary {
  return {
    id: wire.id,
    number: wire.number ?? null,
    status: wire.status as T.QuotationStatus,
    customerId: wire.customerId,
    customerName: wire.customerName,
    date: wire.quoteDate,
    validUntil: wire.validUntil,
    reference: wire.reference ?? null,
    total: money(wire.total),
    invoiceId: wire.invoiceId ?? null,
    invoiceNumber: wire.invoiceNumber ?? null,
  };
}

export function toQuotation(wire: QuotationDetailWire): T.Quotation {
  const { quotation } = wire;
  return {
    ...toQuotationSummary(quotation.summary),
    currency: quotation.currencyCode,
    customerTaxId: quotation.customerTaxId ?? undefined,
    terms: quotation.terms ?? null,
    note: quotation.note ?? null,
    taxExclusive: money(quotation.taxExclusive),
    allowances: money(quotation.allowances),
    charges: money(quotation.charges),
    vat: money(quotation.vat),
    sentAt: quotation.sentAt ?? null,
    lines: quotation.lines.map((line) => ({
      n: Number(line.lineNumber),
      itemName: line.description,
      accountId: line.accountId,
      accountName: wire.accountNames[String(line.lineNumber)] ?? "",
      quantity: plain(line.quantity),
      unitPrice: plain(line.grossPrice),
      vatCategory: VAT_CATEGORY[line.category],
      vatRatePct: line.rate === null || line.rate === undefined ? "0" : plain(line.rate),
      lineNet: money(line.netAmount),
      ...(Number(line.lineCharges) !== 0 ? { charges: money(line.lineCharges) } : {}),
      ...(Number(line.lineAllowances) !== 0 ? { allowances: money(line.lineAllowances) } : {}),
      ...(line.itemCode ? { itemCode: line.itemCode } : {}),
      ...(line.exemptionReason ? { exemptionReason: line.exemptionReason } : {}),
      ...(line.billingItemId ? { billingItemId: line.billingItemId } : {}),
    })),
    adjustments: quotation.adjustments.map((a) => ({
      isCharge: a.isCharge,
      amount: money(a.amount),
      category: VAT_CATEGORY[a.category],
      rate: a.rate === null || a.rate === undefined ? null : plain(a.rate),
      reason: a.reason ?? "",
    })),
    history: { createdBy: "", createdAt: quotation.createdAt },
  };
}

/** What `POST` and `PUT /quotations` take: an invoice's lines, with a quotation's header. */
export function toQuotationRequest(dto: T.NewQuotation, currency: string) {
  const { lines, allowancesAndCharges, exemptionReasons } = toRevision({
    customerId: dto.customerId,
    date: dto.date,
    lines: dto.lines,
    adjustments: dto.adjustments,
  });
  return {
    customerId: dto.customerId,
    quoteDate: dto.date,
    validUntil: dto.validUntil,
    currencyCode: currency,
    reference: dto.reference ?? null,
    terms: dto.terms ?? null,
    note: dto.note ?? null,
    lines,
    allowancesAndCharges,
    ...(exemptionReasons ? { exemptionReasons } : {}),
  };
}

/** A credit note's note carries its reason first ("Goods returned: three cartons"). */
function splitNote(note: string | null | undefined): {
  reason?: T.CreditNoteReason;
  detail?: string;
} {
  if (!note) return {};
  const at = note.indexOf(": ");
  if (at < 0) return { reason: note as T.CreditNoteReason };
  return { reason: note.slice(0, at) as T.CreditNoteReason, detail: note.slice(at + 2) };
}

export function toCreditNoteSummary(wire: DocumentSummaryWire): T.CreditNoteSummary {
  return {
    id: wire.id,
    number: wire.number,
    customerId: wire.customerId,
    customerName: wire.customerName,
    date: wire.issueDate,
    appliedTo: wire.reference ?? null,
    reason: splitNote(wire.note).reason,
    status: wire.issued ? "approved" : "draft",
    total: money(wire.total),
  };
}

export function toInvoiceCredits(wire: Schemas["InvoiceCredits"]): T.InvoiceCredits {
  return {
    available: wire.available.map((c) => ({
      kind: c.kind,
      sourceId: c.sourceId,
      number: c.number,
      date: c.date,
      available: money(c.available),
    })),
    used: wire.used.map((c) => ({
      kind: c.kind,
      sourceId: c.sourceId,
      number: c.number,
      date: c.appliedOn,
      amount: money(c.amount),
    })),
  };
}

export function toCreditNote(wire: DocumentDetailWire): T.CreditNote {
  return {
    ...toCreditNoteSummary(wire.summary),
    ...(wire.creditLeft === null || wire.creditLeft === undefined
      ? {}
      : { creditLeft: money(wire.creditLeft) }),
    detail: splitNote(wire.note).detail,
    customerTaxId: wire.customerTaxId ?? undefined,
    customerLegalRegistration: wire.customerLegalRegistration ?? undefined,
    lines: toLines(wire),
    totals: toTotals(wire),
    history: toHistory(wire),
  };
}

export function toDocumentQuery(q: T.ListQuery): Record<string, string | undefined> {
  return {
    q: q.q,
    from: q.from,
    to: q.to,
    minAmount: q.minAmount,
    payment: q.payment === undefined || q.payment === "all" ? undefined : q.payment,
    // A document on the API is a draft or issued; the mock's review steps have no column behind them.
    issued: q.status === "draft" ? "false" : q.status === "approved" ? "true" : undefined,
    pageSize: q.pageSize === undefined ? undefined : String(q.pageSize),
    cursor: q.cursor,
    customerId: q.customerId,
  };
}

export function toAdditionalCharge(wire: Schemas["AdditionalCharge"]): T.AdditionalCharge {
  return {
    id: wire.id,
    name: wire.name,
    reasonCode: wire.reasonCode ?? "",
    category: VAT_CATEGORY[wire.category],
    rate: wire.rate === null || wire.rate === undefined ? null : plain(wire.rate),
    defaultAmount:
      wire.defaultAmount === null || wire.defaultAmount === undefined
        ? ""
        : money(wire.defaultAmount),
  };
}

export function toPaymentOption(wire: Schemas["PaymentOption"]): T.PaymentOption {
  return {
    id: wire.id,
    kind: wire.kind,
    bankAccountId: wire.bankAccountId,
    bankAccountName: wire.bankAccountName,
    accountNumber: wire.accountNumber ?? undefined,
    ledgerAccountId: wire.ledgerAccountId,
    name: wire.name ?? undefined,
    payee: wire.payee ?? undefined,
    shownOnInvoice: wire.shownOnInvoice,
  };
}

export function toPaymentOptionRequest(option: T.PaymentOptionForm) {
  return {
    kind: option.kind,
    bankAccountId: option.bankAccountId,
    name: option.name ?? null,
    payee: option.payee ?? null,
    shownOnInvoice: option.shownOnInvoice,
  };
}

export function toAdditionalChargeRequest(charge: Omit<T.AdditionalCharge, "id">) {
  return {
    name: charge.name,
    category: CATEGORY_ON_THE_WIRE[charge.category],
    reasonCode: charge.reasonCode || null,
    rate: charge.category === "S" ? charge.rate : null,
    defaultAmount: charge.defaultAmount || null,
  };
}

/** What `POST /invoices` takes; the customer's details come from the party record, never from here. */
export function toNewInvoice(dto: T.NewInvoice, currency: string) {
  return {
    issueDate: dto.date,
    currencyCode: currency,
    customerId: dto.customerId,
    dueDate: dto.due ?? null,
    paymentTermsDays: dto.paymentTermsDays === undefined ? null : Number(dto.paymentTermsDays),
    buyerReference: dto.buyerReference ?? null,
    note: dto.note ?? null,
  };
}

/** What `PUT /invoices/{id}` takes. A line with no tax has no rate, not a rate of zero. */
export function toRevision(dto: T.NewInvoice) {
  // A document has one reason per category (BT-120), so the first line's stands for the others.
  const reasons: Partial<Record<Schemas["VatCategory"], string>> = {};
  for (const line of dto.lines) {
    const category = CATEGORY_ON_THE_WIRE[line.vatCategory];
    if (line.exemptionReason && !reasons[category]) reasons[category] = line.exemptionReason;
  }

  return {
    ...(Object.keys(reasons).length > 0 ? { exemptionReasons: reasons } : {}),
    lines: dto.lines.map((line, index) => ({
      lineNumber: index + 1,
      description: line.description,
      quantity: line.quantity,
      grossPrice: line.unitPrice,
      category: CATEGORY_ON_THE_WIRE[line.vatCategory],
      accountId: line.accountId,
      // Only outside scope has no rate. Issuing refuses any other category without one, and only
      // standard has a rate above zero.
      rate: line.vatCategory === "O" ? null : line.vatCategory === "S" ? line.ratePct : "0",
      itemCode: line.itemCode || null,
      billingItemId: line.billingItemId ?? null,
      lineCharges: line.charges || null,
      lineAllowances: line.allowances || null,
    })),
    allowancesAndCharges: (dto.adjustments ?? []).map((a) => ({
      isCharge: a.isCharge,
      amount: a.amount,
      category: CATEGORY_ON_THE_WIRE[a.category],
      rate: a.category === "O" ? null : a.category === "S" ? a.rate : "0",
      reason: a.reason,
    })),
    cashSale: dto.cashSale
      ? {
          amountReceived: dto.cashSale.amountReceived,
          paymentMethod: dto.cashSale.method,
          bankAccountId: dto.cashSale.bankAccountId,
        }
      : null,
  };
}

/**
 * A credit note has no form field for BR-CO-25's reference, because it already says what it refers
 * to: the invoice it corrects, or failing that the reason it exists.
 */
export function toNewCreditNote(
  dto: T.NewCreditNote,
  today: T.IsoDate,
  currency: string,
  correctsNumber?: string | null,
) {
  return {
    issueDate: today,
    currencyCode: currency,
    customerId: dto.customerId,
    buyerReference: correctsNumber ?? dto.reason,
    note: dto.detail ? `${dto.reason}: ${dto.detail}` : dto.reason,
  };
}

export function toCreditNoteRevision(
  dto: T.NewCreditNote,
  corrects?: { id: T.Id; number: string },
) {
  return {
    ...toRevision({
      customerId: dto.customerId,
      date: "",
      lines: dto.lines.map((line) => ({ ...line, description: line.itemName })),
    }),
    references:
      corrects === undefined ? null : [{ number: corrects.number, invoiceId: corrects.id }],
  };
}

function paisa(value: unknown): number {
  return Math.round(Number(value) * 100);
}

/** What arrived in the bank: the gross the debt was reduced by, less what the customer withheld. */
export function toReceiptSummary(wire: ReceiptSummaryWire): T.ReceiptSummary {
  return {
    id: wire.id,
    number: wire.number,
    customerId: wire.customerId,
    customerName: wire.customerName,
    date: wire.receivedOn,
    method: (wire.paymentMethod ?? undefined) as T.ReceiptMethod | undefined,
    appliedTo: wire.appliedTo ?? "Advance",
    status: wire.posted ? "approved" : "draft",
    amount: ((paisa(wire.grossAmount) - paisa(wire.withholdingAmount)) / 100).toFixed(2),
  };
}

export function toReceipt(wire: ReceiptDetailWire): T.Receipt {
  const { receipt } = wire;
  const applied = receipt.applications.reduce((sum, a) => sum + paisa(a.amount), 0);
  return {
    ...toReceiptSummary(receipt.summary),
    bankAccountId: wire.bankAccountId ?? "",
    bankAccountName: wire.bankAccountName ?? "",
    applications: receipt.applications.map((a) => ({
      invoiceId: a.invoiceId,
      invoiceNumber: a.invoiceNumber ?? "",
      invoiceBalance: money(a.invoiceOutstanding),
      applied: money(a.amount),
      // Held per receipt, not per invoice: the API stores one withheld figure.
      tdsWithheld: "0.00",
    })),
    // What is left of it to use. A draft has not become an advance yet.
    advance: receipt.summary.posted
      ? money(receipt.advanceLeft)
      : ((paisa(receipt.summary.grossAmount) - applied) / 100).toFixed(2),
    note: receipt.note ?? undefined,
    history: {
      createdBy: "",
      createdAt: receipt.createdAt,
      ...(receipt.postedAt === null ? {} : { approvedAt: receipt.postedAt }),
    },
  };
}

/** The gross is the debt discharged: the cash plus what the customer withheld (RULE 4.2). */
export function toNewReceipt(dto: T.NewReceipt, depositAccountId: T.Id, currency: string) {
  const withheld = dto.applications.reduce((sum, a) => sum + paisa(a.tdsWithheld), 0);
  return {
    receivedOn: dto.date,
    currencyCode: currency,
    customerId: dto.customerId,
    depositAccountId,
    grossAmount: ((paisa(dto.amount) + withheld) / 100).toFixed(2),
    withholdingAmount: (withheld / 100).toFixed(2),
    paymentMethod: dto.method,
    note: dto.note ?? null,
  };
}
